import { useState, useEffect } from 'react';
import { useDemo } from '@/contexts/DemoContext';
import { useNavigate } from 'react-router-dom';
import { Users, RotateCcw, LogOut, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { haptics } from '@/utils/haptics';
import { toast } from 'sonner';

/**
 * DemoBanner - Premium demo experience banner
 * 
 * IMPORTANT: This banner is part of normal document flow (NOT fixed positioned)
 * to avoid overlapping the home screen header and content.
 * 
 * Features:
 * - "The Carter Family" messaging (demo mode only)
 * - Soft lavender/brand purple gradient
 * - Collapse/expand toggle with smooth transition
 * - Reset Demo and Exit Demo buttons with accessible labels
 * - 44x44pt minimum touch targets
 * - Hidden for real user accounts
 */

const DemoBanner = () => {
  const { isDemoMode, exitDemoMode, resetDemoData } = useDemo();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Return null early if not in demo mode (hidden for real accounts)
  if (!isDemoMode) return null;

  const handleExit = () => {
    haptics.tap();
    exitDemoMode();
    toast.success('Demo ended', {
      description: 'Create your own account to get started!'
    });
    navigate('/login');
  };

  const handleReset = () => {
    haptics.tap();
    resetDemoData();
    toast.success('Demo reset', {
      description: 'All sample data has been restored'
    });
    window.location.reload();
  };

  const toggleCollapse = () => {
    haptics.tap();
    setIsAnimating(true);
    setIsCollapsed(!isCollapsed);
    // Reset animation flag after transition
    setTimeout(() => setIsAnimating(false), 300);
  };

  // Collapsed state - minimal strip
  if (isCollapsed) {
    return (
      <div 
        className={`bg-gradient-to-r from-purple-500 via-violet-500 to-purple-500 text-white shadow-sm transition-all duration-300 ease-out ${isAnimating ? 'opacity-90' : 'opacity-100'}`}
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)'
        }}
        data-testid="demo-mode-banner"
        role="banner"
        aria-label="Demo mode banner - collapsed"
      >
        <div className="max-w-2xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-white/90" aria-hidden="true" />
              <span className="text-xs font-semibold text-white">Demo Mode</span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                className="h-11 w-11 p-0 text-white/90 hover:text-white hover:bg-white/20 rounded-full"
                data-testid="demo-reset-btn"
                aria-label="Reset demo data"
                title="Reset demo data"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleExit}
                className="h-11 w-11 p-0 text-white/90 hover:text-white hover:bg-white/20 rounded-full"
                data-testid="demo-exit-btn"
                aria-label="Exit demo mode"
                title="Exit demo mode"
              >
                <LogOut className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleCollapse}
                className="h-11 w-11 p-0 text-white/90 hover:text-white hover:bg-white/20 rounded-full"
                data-testid="demo-expand-btn"
                aria-label="Expand demo banner"
                title="Expand banner"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Expanded state - full banner with description (demo mode only)
  return (
    <div 
      className={`bg-gradient-to-r from-purple-500 via-violet-500 to-purple-500 text-white shadow-md transition-all duration-300 ease-out ${isAnimating ? 'opacity-90' : 'opacity-100'}`}
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)'
      }}
      data-testid="demo-mode-banner"
      role="banner"
      aria-label="Demo mode banner - The Carter Family sample data"
    >
      <div className="max-w-2xl mx-auto px-4 py-2.5">
        {/* Single responsive layout that works on all screens */}
        <div className="flex items-center justify-between gap-3">
          {/* Left: Family icon and name */}
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div 
              className="flex items-center justify-center w-8 h-8 rounded-full bg-white/25 flex-shrink-0"
              aria-hidden="true"
            >
              <Users className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <span className="font-semibold text-sm text-white block">The Carter Family</span>
              {/* Demo-only subtitle - less prominent */}
              <span className="text-[10px] text-white/70 leading-tight block">
                Sample emergency binder · Tap reset to restore
              </span>
            </div>
          </div>

          {/* Right: Controls with 44x44 touch targets */}
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="h-11 w-11 p-0 text-white/90 hover:text-white hover:bg-white/20 rounded-full"
              data-testid="demo-reset-btn"
              aria-label="Reset demo data to original state"
              title="Reset demo"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExit}
              className="h-11 w-11 p-0 text-white hover:bg-white/20 rounded-full"
              data-testid="demo-exit-btn"
              aria-label="Exit demo mode and return to login"
              title="Exit demo"
            >
              <LogOut className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleCollapse}
              className="h-11 w-11 p-0 text-white/90 hover:text-white hover:bg-white/20 rounded-full"
              data-testid="demo-collapse-btn"
              aria-label="Collapse demo banner"
              title="Collapse banner"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoBanner;
